import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { useSignInMutation } from '../../apis/mlkApi/mlk.api.hooks';
import { Button } from '../../components/Button/Button';
import { GoogleButton } from '../../components/GoogleButton/GoogleButton';
import { BaseLayout } from '../../components/Layout/Layout';
import { SeparatorWithText } from '../../components/SeparatorWithText/SeparatorWithText';
import { TextField } from '../../components/TextField/TextField';
import { useLanguage } from '../../locale/useLanguage';
import { emailRegexp } from '../../helpers/regexp';
import { Routes } from '../../routes/types';

import './SignInView.css';
import { useSelector } from 'react-redux';
import { getOperationSelector } from '../../redux/general/general.selectors';
import { useBack } from '../../hooks/useBack';
import { parseRes } from '../../apis/utils';
import { handleSignIn } from '../../helpers/auth';
import { FaqFeature } from '../../models';

type SignInViewProps = {};

type SignInForm = {
  email: string;
  password: string;
};

export const SignInView: React.FC<SignInViewProps> = () => {
  const [t] = useLanguage();
  const history = useHistory();

  const [signInRequest] = useSignInMutation();
  const operation = useSelector(getOperationSelector);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInForm>();

  const goBack = useBack();

  const goToSignUp = useCallback(() => {
    history.push(Routes.SignUp);
  }, [history]);

  const goToForgetPassword = useCallback(() => {
    history.push(Routes.RecoverPasswordInsertEmail);
  }, [history]);

  const signIn = useCallback(
    (formData: SignInForm) => {
      signInRequest(formData).then((res) => {
        const { successRes, errorRes } = parseRes(res);
        handleSignIn(successRes, operation, history);
        if (errorRes.error) {
          // TODO: Handle error
        }
      });
    },
    [history, operation, signInRequest]
  );

  const submit = useMemo(() => handleSubmit(signIn), [handleSubmit, signIn]);

  const emailValidator = useMemo(
    () => ({
      ...register('email', {
        required: t('signInView.emailError'),
        pattern: {
          value: emailRegexp,
          message: t('signInView.emailError'),
        },
      }),
    }),
    [register, t]
  );

  const passwordValidator = useMemo(
    () => ({
      ...register('password', {
        required: t('signInView.passwordError'),
      }),
    }),
    [register, t]
  );

  const goToInfo = useCallback(() => {
    history.push(Routes.Faq.replace(':feature', FaqFeature.Login));
  }, [history]);

  return (
    <BaseLayout
      hasInfo
      onInfoClick={goToInfo}
      hasBack
      onBackClick={goBack}
      title={t('pageTitles.signIn')}
    >
      <form onSubmit={submit}>
        <TextField
          {...emailValidator}
          type="email"
          placeholder={t('signInView.email')}
          error={errors.email?.message}
        />
        <TextField
          {...passwordValidator}
          type="password"
          placeholder={t('signInView.password')}
          error={errors.password?.message}
        />
        <div className="sign-in--button-container">
          <Button type="submit" label={t('signInView.signIn')} isPrimary />
          <div className="sign-in--second-button">
            <Button
              onClick={goToSignUp}
              type="button"
              label={t('signInView.signUp')}
            />
          </div>
        </div>
      </form>
      <div className="sign-in--forget-password">
        <a onClick={goToForgetPassword} className="link">
          {t('signInView.forgetPassword')}
        </a>
      </div>
      <SeparatorWithText text={t('signInView.signInWith')} />
      <GoogleButton />
      {/* <FacebookButton />
        <div className="sign-in--second-button">
          <GoogleButton />
        </div> */}
    </BaseLayout>
  );
};
